import { useEffect, useState } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

export const INCIDENT_CATEGORIES = ["Unplanned hospitalization", "Fall", "Injury", "Medication issue", "Property damage", "Other"];
export const COMPLAINT_CATEGORIES = ["Service quality", "Caregiver concern", "Scheduling", "Billing", "Communication", "Other"];

const RadioRow = ({ label, options, value, onChange, tid }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex flex-wrap gap-1.5" data-testid={tid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          data-testid={`${tid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-md border px-3 py-1.5 text-sm transition-all ${
            value === o ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-800"
          }`}>{o}</button>
      ))}
    </div>
  </div>
);

export const IncidentReportDialog = ({ clientId, onReported, buttonTestId = "report-incident-button",
                                       externalOpen, onExternalClose, initialCategory = "", initialDescription = "" }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = externalOpen !== undefined;
  const open = controlled ? externalOpen : internalOpen;
  const setOpen = (v) => {
    if (controlled) {
      if (!v) onExternalClose?.();
    } else {
      setInternalOpen(v);
    }
  };
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (controlled && externalOpen) {
      setKind("incident");
      setCategory(initialCategory);
      setDescription(initialDescription);
      setDate(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const categories = kind === "incident" ? INCIDENT_CATEGORIES : kind === "complaint" ? COMPLAINT_CATEGORIES : [];

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/incidents`, { kind, category, date, description });
      toast.success(kind === "incident" && category === "Unplanned hospitalization"
        ? "Incident reported — client flagged for care plan review"
        : "Report filed");
      setOpen(false);
      setKind(""); setCategory(""); setDescription("");
      onReported?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-[#C0392B] hover:text-white" data-testid={buttonTestId}>
            <WarningCircle size={16} className="mr-1.5" /> Report Incident / Complaint
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-md" data-testid="incident-dialog">
        <DialogHeader>
          <DialogTitle>Report an incident or complaint</DialogTitle>
          <DialogDescription>
            Incidents like an unplanned hospitalization automatically flag this client for a care plan review. The flag clears once a reassessment or approval is completed after the event.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RadioRow label="What are you reporting?" options={["Incident", "Complaint"]}
            value={kind === "incident" ? "Incident" : kind === "complaint" ? "Complaint" : ""}
            onChange={(v) => { setKind(v.toLowerCase()); setCategory(""); }} tid="inc-kind" />
          {kind && (
            <RadioRow label={kind === "incident" ? "Incident type" : "Complaint about"} options={categories}
              value={category} onChange={setCategory} tid="inc-category" />
          )}
          {category === "Unplanned hospitalization" && (
            <p className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 px-3 py-2 text-xs text-[#C0392B]" data-testid="hosp-review-note">
              This will flag the client for a care plan review on the dashboard.
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-stone-300 bg-white" data-testid="inc-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened, who was involved, immediate actions taken..."
              className="border-stone-300 bg-white" data-testid="inc-description" />
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={busy || !kind || !category || !date}
              className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="inc-submit">
              {busy ? "Filing..." : "File Report"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
